import { FC } from 'react';
import {
  ActionsMenu,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  TruncatedText,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { Divider, List, ListItem, Stack } from '@mui/material';
import {
  COLOR_BY_STATUS,
  COMPANY_EXPENSE_ID,
  claimMismatches,
  claimStatuses,
  claimsFiltersSchema,
  useClaims,
  useExportToXls,
  useUpdateClaim,
  useUpdateClaims,
} from 'store/claims';
import { APP, ROUTES } from 'config';
import { DownloadXlsAction } from 'components/actions';
import { useDefaultCurrency, useMe } from 'state/Administrators';
import moment from 'moment';
import { formatCurrency } from 'utils/format';
import { useModalContext } from 'store/modal';
import { usePredefinedToasts } from 'utils/Toast';
import {
  BenefitsFilter,
  CompaniesFilter,
  DateRangeFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { useSearchParams } from 'utils/navigation';
import { RECOGNITION_SHOP_ID } from 'store/recognitions';
import { ClaimRejectModal } from 'containers/claims';
import { EmployeeCell } from 'components/table';

export const ClaimsList: FC = () => {
  const { t, i18n } = useTranslation();
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();
  const { currency } = useDefaultCurrency();
  const { hasViewPermission, hasEditPermission } = useMe();
  const { searchParams: filters, navigate } =
    useSearchParams(claimsFiltersSchema);

  const { data: claims, isLoading } = useClaims(filters);

  const { mutate: updateClaim, isLoading: isUpdatingClaim } = useUpdateClaim();
  const { mutate: updateClaims, isLoading: isUpdatingClaims } =
    useUpdateClaims();
  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <ListTotal total={claims?.total} />

          <Stack direction="row" alignItems="center" gap={1}>
            <Filters
              value={filters}
              initialValues={claimsFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              onChangeCallback={() => setSelectedRowIds({})}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('claims.filters.mismatch.label')}
                    value={value.mismatch}
                    initialValue={initialValues.mismatch}
                    onChange={(v) => setFilter('mismatch', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={claimMismatches.map((mismatch) => ({
                          key: mismatch,
                          label: t(`claims.mismatch.${mismatch}`),
                        }))}
                        searchable={false}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('employees.company')}
                    value={value.company}
                    initialValue={initialValues.company}
                    onChange={(v) => setFilter('company', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <CompaniesFilter
                        value={value}
                        onApplyFilter={applyFilter}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('claims.filters.claim_date.label')}
                    value={value.claimDate}
                    initialValue={initialValues.claimDate}
                    onChange={(v) => setFilter('claimDate', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <DateRangeFilter
                        value={value}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('claims.table.purchase_date.title')}
                    value={value.purchaseDate}
                    initialValue={initialValues.purchaseDate}
                    onChange={(v) => setFilter('purchaseDate', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <DateRangeFilter
                        value={value}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('expenses.expense_date')}
                    value={value.expenseDate}
                    initialValue={initialValues.expenseDate}
                    onChange={(v) => setFilter('expenseDate', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <DateRangeFilter
                        value={value}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  {hasViewPermission(['benefits']) ? (
                    <FilterButton
                      label={t('global.common.benefit_name')}
                      value={value.benefit}
                      initialValue={initialValues.benefit}
                      onChange={(v) => setFilter('benefit', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <BenefitsFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>
                  ) : null}

                  <FilterButton
                    label={t('common.status')}
                    value={value.status}
                    initialValue={initialValues.status}
                    onChange={(v) => setFilter('status', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={claimStatuses.map((status) => ({
                          key: status,
                          label: t(`claims.status.${status}`),
                        }))}
                        searchable={false}
                      />
                    )}
                  </FilterButton>

                  {hasViewPermission(['employees']) ? (
                    <OpenSearchFilter
                      value={value.search}
                      onApplyFilter={(v) => setFilter('search', v)}
                      placeholder={t('claims.filters.search.placeholder')}
                    />
                  ) : null}
                </>
              )}
            </Filters>

            <Divider orientation="vertical" variant="middle" flexItem />

            <DownloadXlsAction
              listLength={claims?.total ?? 0}
              exportToExcel={() => exportToXls({ filters })}
              loading={isExportingToXls}
            />
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={claims?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'employee',
            header: t('entity_type.employee'),
            cell: ({ row: { original: claim } }) => (
              <EmployeeCell employee={claim.employee} />
            ),
          },
          {
            id: 'claimedAt',
            header: t('claims.table.claim_date.title'),
            cell: ({ row: { original: claim } }) =>
              hasViewPermission(['employees']) ? (
                <Link
                  label={moment(claim.claimedAt).format('l LT')}
                  to={ROUTES.claims.details.replace(':id', claim.id)}
                />
              ) : (
                moment(claim.claimedAt).format('l LT')
              ),
          },
          {
            id: 'purchasedAt',
            header: t('claims.table.purchase_date.title'),
            cell: ({ row: { original: claim } }) =>
              moment(claim.purchasedAt).format('l'),
          },
          {
            id: 'expenseDate',
            header: t('expenses.expense_date'),
            cell: ({ row: { original: claim } }) =>
              claim.expenseDate ? moment(claim.expenseDate).format('l') : '-',
          },
          {
            id: 'source',
            header: t('wallet.employeeWalletFundsSource'),
            cell: ({ row: { original: claim } }) => (
              <List sx={{ py: 0 }}>
                {claim.sources.map((s) => (
                  <ListItem sx={{ p: 0, mb: 1 }} key={s.id}>
                    {s.id === RECOGNITION_SHOP_ID ? (
                      hasViewPermission(['employeeRecognition']) ? (
                        <Link
                          label={s.name}
                          to={ROUTES.recognitions.settings}
                        />
                      ) : (
                        <Typography>{s.name}</Typography>
                      )
                    ) : s.id === COMPANY_EXPENSE_ID ? (
                      <Link label={s.name} to={ROUTES.claims.settings} />
                    ) : hasViewPermission(['benefits']) ? (
                      <Link
                        label={s.name}
                        to={ROUTES.benefits.details.replace(':id', s.id)}
                      />
                    ) : (
                      <Typography>{s.name}</Typography>
                    )}
                  </ListItem>
                ))}
              </List>
            ),
          },
          {
            id: 'supplier',
            header: t('marketplaceItems.supplier'),
            cell: ({ row: { original: claim } }) => {
              return <TruncatedText text={claim.supplier} maxLength={25} />;
            },
          },
          {
            id: 'requestedAmount',
            header: t('claims.table.requested_amount.title'),
            meta: { align: 'right' },
            cell: ({ row: { original: claim } }) =>
              formatCurrency(i18n.language, claim.requestedAmount, currency),
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: claim } }) => {
              return (
                <Typography color={COLOR_BY_STATUS[claim.status]}>
                  {t(`claims.status.${claim.status}`)}
                </Typography>
              );
            },
          },
          {
            id: 'actions',
            cell: ({ row: { original: claim } }) => {
              if (claim.status === 'pendingHRAction') {
                return (
                  <ActionsMenu
                    type="compact"
                    items={[
                      {
                        label: t('global.actions.approve'),
                        disabled: !hasEditPermission(['expenseCompensations']),
                        onClick: () => {
                          updateClaim(
                            { claimId: claim.id, data: { status: 'approved' } },
                            {
                              onSuccess: () =>
                                predefinedToasts.success.updated(),
                            },
                          );
                        },
                      },
                      {
                        label: t('claims.actions.request_extra_input'),
                        disabled: !hasEditPermission(['expenseCompensations']),
                        onClick: async () => {
                          const response = await openModal({
                            component: ClaimRejectModal,
                            props: { title: claim.supplier, action: 'request' },
                          });

                          if (response.action === 'REJECT') {
                            updateClaim({
                              claimId: claim.id,
                              data: {
                                status: response.payload.status,
                                adminComment: response.payload.comment,
                              },
                            });
                          }
                        },
                      },
                      {
                        label: t('global.actions.reject'),
                        disabled: !hasEditPermission(['expenseCompensations']),
                        onClick: async () => {
                          const response = await openModal({
                            component: ClaimRejectModal,
                            props: { title: claim.supplier, action: 'reject' },
                          });

                          if (response.action === 'REJECT') {
                            updateClaim({
                              claimId: claim.id,
                              data: {
                                status: response.payload.status,
                                adminComment: response.payload.comment,
                              },
                            });
                          }
                        },
                        color: 'error',
                      },
                    ]}
                  />
                );
              }

              if (claim.status === 'pendingEmployeeAction') {
                return (
                  <ActionsMenu
                    type="compact"
                    items={[
                      {
                        label: t('global.actions.approve_amount', {
                          amount: formatCurrency(
                            i18n.language,
                            claim.requestedAmount,
                            currency,
                          ),
                        }),
                        disabled: !hasEditPermission(['expenseCompensations']),
                        onClick: () => {
                          updateClaim(
                            { claimId: claim.id, data: { status: 'approved' } },
                            {
                              onSuccess: () =>
                                predefinedToasts.success.updated(),
                            },
                          );
                        },
                      },
                      {
                        label: t('global.actions.reject'),
                        disabled: !hasEditPermission(['expenseCompensations']),
                        onClick: async () => {
                          const response = await openModal({
                            component: ClaimRejectModal,
                            props: { title: claim.supplier, action: 'reject' },
                          });

                          if (response.action === 'REJECT') {
                            updateClaim({
                              claimId: claim.id,
                              data: {
                                status: response.payload.status,
                                adminComment: response.payload.comment,
                              },
                            });
                          }
                        },
                        color: 'error',
                      },
                    ]}
                  />
                );
              }

              return null;
            },
          },
        ]}
        pagination={
          claims && {
            count: claims.total,
            page: claims.page,
            pageSize: claims.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          isLoading: isUpdatingClaims || isUpdatingClaim,
          actions: [
            {
              label: t('claims.actions.mark_as_paid'),
              disabled: !hasEditPermission(['expenseCompensations']),
              onClick: () => {
                updateClaims(
                  {
                    updates: Object.keys(selectedRowIds).map((claimId) => ({
                      claimId,
                      status: 'paid',
                    })),
                  },
                  {
                    onSuccess: () => {
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            },
            {
              label: t('claims.actions.mark_as_approved'),
              disabled: !hasEditPermission(['expenseCompensations']),
              onClick: () => {
                updateClaims(
                  {
                    updates: Object.keys(selectedRowIds).map((claimId) => ({
                      claimId,
                      status: 'approved',
                    })),
                  },
                  {
                    onSuccess: () => {
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            },
          ],
        }}
      />
    </TableWrapper>
  );
};
